// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Stage 2b of the Power Flow model: summarising repeated terminal loads.
 *
 * See docs/power-flow/power-flow-single-line-diagram.md §7.6.
 *
 * **Why this stage exists.** The deepest rank is the ruler the whole drawing is
 * measured against — `powerFlowLayout` packs it at a fixed pitch and centres
 * every shallower node over its own children. A site with sixty IT racks
 * therefore produces a canvas sixty chips wide, and every rank above it is
 * stretched across that width, which is what turned the first render into a
 * wall of identical boxes joined by conductors running the full width of the
 * stage. The mock does not draw racks at all: it writes `RPP × 4` under the PDU
 * that feeds them.
 *
 * So: a run of identical terminal loads hanging off the same feed becomes one
 * **group** node carrying a count. This is a *display* transform and nothing
 * else — it runs after ranking, it cannot change anyone's electrical depth, and
 * it is reversible per group by the reader.
 *
 * **Grouping is keyed on the parent set, not on a single parent.** IT racks are
 * dual-corded: a rack fed by `RPP-A1` *and* `RPP-B1` has two upstream
 * conductors, and collapsing it under one of them would silently delete the
 * other — on a one-line, the drawing's answer to "if this fails, what goes
 * dark?" would flip from "nothing" to "this rack". Bucketing by
 * `(entityType, sorted parent ids)` keeps that honest: the A/B pair collapses
 * into one group that both boards feed, and a rack wired differently from its
 * neighbours is left as its own chip precisely *because* it is different.
 *
 * Everything here is deterministic — ids are built from sorted member and
 * parent lists, and every returned list is sorted.
 */

import type { PowerFlowGraph, PowerFlowLink, PowerFlowVertex } from './powerFlowGraph';
import type { PowerFlowRanking } from './powerFlowRank';

/** A run of identical terminal loads sharing one set of upstream feeds. */
export interface PowerFlowLoadGroup {
  /** `group:<entityType>:<sorted parent ids>`. Stable across polls. */
  id: string;
  /** The entity type every member shares. */
  entityType: string;
  /** Member asset ids, sorted. */
  memberIds: string[];
  /** The upstream assets that feed every member, sorted. */
  parentIds: string[];
  /** Electrical depth, inherited from the members (they all share it). */
  rank: number;
  /** True when the reader has fanned this group back out into chips. */
  expanded: boolean;
  /**
   * What the members are. `load` is a run of terminal loads (racks); `bus` is a
   * run of light buses (RPPs) that became terminal once their own loads were
   * folded away. Both draw as one badge with a count.
   */
  kind: 'load' | 'bus';
  /**
   * True when the group is not drawn at all and instead annotates the bars
   * that feed it — see `foldTerminalLoads`.
   */
  folded: boolean;
}

/** Terminal loads folded into a drawn asset, for its sub-label. */
export interface FoldedLoads {
  entityType: string;
  count: number;
  /**
   * The folded assets themselves, sorted. `count` is `memberIds.length`, kept
   * for the sub-label; the ids are what lets the renderer look the members'
   * live status up, so a rack in alarm behind an `RPP` box is not hidden by
   * the fold — the box takes the worst status among them.
   */
  memberIds: string[];
}

/**
 * The diagram as it will actually be drawn: real vertices, minus the members
 * hidden behind a collapsed group, plus one synthetic vertex per group.
 *
 * Shaped to be a drop-in for what `powerFlowLayout` used to read off
 * `PowerFlowGraph` + `PowerFlowRanking`, so layout is not aware that grouping
 * happened — a group is just another node with a width and a rank.
 */
export interface PowerFlowDisplay {
  groups: PowerFlowLoadGroup[];
  groupsById: Map<string, PowerFlowLoadGroup>;
  /** Member id → the group standing in for it. Collapsed groups only. */
  groupOf: Map<string, string>;
  /** Members currently hidden behind a collapsed group. */
  collapsedIds: Set<string>;
  /** Vertices to draw, including a synthetic one per collapsed group. */
  verticesById: Map<string, PowerFlowVertex>;
  /** Node ids per rank. Members of a collapsed group are replaced by the group. */
  ranks: string[][];
  rankOf: Map<string, number>;
  /** Downward conductors, with member endpoints rewritten to their group. */
  treeLinks: PowerFlowLink[];
  /** Ties, likewise rewritten. Sorted by id. */
  cycleLinks: PowerFlowLink[];
  /**
   * Drawn node id → the terminal loads folded beneath it, by type. An RPP bar
   * reads `4 racks`; an `RPP × 3` badge reads `12 racks`. Empty unless
   * `foldTerminalLoads` is on.
   */
  foldedLoadsOf: Map<string, FoldedLoads[]>;
}

export interface CollapsePowerFlowOptions {
  /**
   * Group ids the reader has expanded. An expanded group draws its members as
   * individual chips and contributes no group node.
   */
  expanded?: ReadonlySet<string>;
  /**
   * Fewest identical loads that are worth summarising.
   *
   * Three, not two: a pair of chips side by side is already legible and reads
   * more honestly than `IT Rack × 2`, which costs the same horizontal space and
   * says less. Set to `Infinity` to disable grouping entirely.
   */
  minGroupSize?: number;
  /**
   * Fold every collapsed load group into the bars that feed it instead of
   * drawing it, then group the light buses that this leaves terminal.
   *
   * This is the mock's own convention, and the reference's: neither draws a
   * rack. The mock writes `RPP × 4` under the PDU; the reference stops at the
   * PDU. On GA-10 the racks are the deepest rank, they publish no power, every
   * one is dual-corded *across blocks*, and there are 182 of them — so drawn
   * as nodes they set the ruler for the whole drawing and run conductors the
   * full width of it. Folded, a rack becomes a count on the RPP that feeds it,
   * and 90 RPP bars become 36 `RPP × n` badges, one under each PDU.
   *
   * Nothing is hidden from the model: the racks are still vertices, still in
   * the KPIs and the detail panel, and the reader can switch the fold off.
   */
  foldTerminalLoads?: boolean;
  /**
   * After folding, badge the light buses left terminal as `RPP × n` under
   * their feed. Defaults to `foldTerminalLoads`. The sheet layout turns it off
   * for a site with blocks, because there it stacks the RPPs vertically under
   * their PDU instead, as the site's own Ignition sheets do.
   */
  groupTerminalBuses?: boolean;
}

export const DEFAULT_MIN_GROUP_SIZE = 3;

/**
 * Collapse repeated terminal loads.
 *
 * A load is collapsible when the ontology calls it terminal (`role: 'load'`),
 * the ranking left it with no downstream conductor, and it has at least one
 * upstream. Anything else — a "load" that feeds something, an orphan with no
 * parent — is a data problem the model already reports, and hiding it inside a
 * count would bury exactly the thing worth seeing.
 */
export const collapsePowerFlow = (
  graph: PowerFlowGraph,
  ranking: PowerFlowRanking,
  options: CollapsePowerFlowOptions = {}
): PowerFlowDisplay => {
  const expanded = options.expanded ?? new Set<string>();
  const minGroupSize = options.minGroupSize ?? DEFAULT_MIN_GROUP_SIZE;
  const fold = options.foldTerminalLoads ?? false;
  const groupBuses = options.groupTerminalBuses ?? fold;

  const { verticesById } = graph;
  const { rankOf, treeLinks, cycleLinks } = ranking;

  // Tree adjacency, from the ranking's acyclic remainder. A vertex's own
  // `in`/`out` still carry the tie edges the ranking removed, and a tie must
  // not decide whether something is a leaf.
  const treeChildren = new Map<string, string[]>();
  const treeParents = new Map<string, string[]>();
  for (const vertex of graph.vertices) {
    treeChildren.set(vertex.id, []);
    treeParents.set(vertex.id, []);
  }
  for (const link of treeLinks) {
    treeChildren.get(link.source)?.push(link.target);
    treeParents.get(link.target)?.push(link.source);
  }

  // --- Buckets -----------------------------------------------------------
  const buckets = new Map<
    string,
    { entityType: string; parentIds: string[]; memberIds: string[] }
  >();

  for (const vertex of graph.vertices) {
    if (vertex.role !== 'load') continue;
    if ((treeChildren.get(vertex.id) ?? []).length > 0) continue;
    const parentIds = (treeParents.get(vertex.id) ?? []).slice().sort();
    if (parentIds.length === 0) continue;

    const key = `group:${vertex.entityType}:${parentIds.join('+')}`;
    const bucket = buckets.get(key);
    if (bucket) bucket.memberIds.push(vertex.id);
    else buckets.set(key, { entityType: vertex.entityType, parentIds, memberIds: [vertex.id] });
  }

  const groups: PowerFlowLoadGroup[] = Array.from(buckets.entries())
    .filter(([, bucket]) => bucket.memberIds.length >= minGroupSize)
    .map(([id, bucket]) => ({
      id,
      entityType: bucket.entityType,
      memberIds: bucket.memberIds.slice().sort(),
      parentIds: bucket.parentIds,
      // Every member shares a parent set, so longest-path ranking gave them all
      // the same depth. `max` rather than `[0]` so a ranking bug shows up as a
      // group drawn too low rather than as a conductor pointing upward.
      rank: bucket.memberIds.reduce((max, id2) => Math.max(max, rankOf.get(id2) ?? 0), 0),
      expanded: expanded.has(id),
      kind: 'load' as const,
      // A collapsed load group folds into its feeders; an expanded one is drawn
      // as its members, so there is nothing to fold.
      folded: fold && !expanded.has(id),
    }))
    .sort((a, b) => (a.id < b.id ? -1 : 1));

  const groupOf = new Map<string, string>();
  const collapsedIds = new Set<string>();
  for (const group of groups) {
    if (group.expanded) continue;
    for (const memberId of group.memberIds) {
      groupOf.set(memberId, group.id);
      collapsedIds.add(memberId);
    }
  }

  // --- Folding -----------------------------------------------------------
  // A folded group leaves the canvas and becomes a count on each bar that
  // feeds it. Its conductors go with it: a rack's two cords, one to each
  // block, were most of the horizontal clutter on a real site.
  const foldedGroupIds = new Set(groups.filter(g => g.folded).map(g => g.id));
  const foldedLoadsOf = new Map<string, FoldedLoads[]>();
  const addFolded = (drawnId: string, entityType: string, memberIds: readonly string[]) => {
    const list = foldedLoadsOf.get(drawnId) ?? [];
    const existing = list.find(entry => entry.entityType === entityType);
    if (existing) {
      existing.memberIds = Array.from(new Set(existing.memberIds.concat(memberIds))).sort();
      existing.count = existing.memberIds.length;
    } else {
      list.push({ entityType, count: memberIds.length, memberIds: memberIds.slice().sort() });
    }
    foldedLoadsOf.set(drawnId, list);
  };
  for (const group of groups) {
    if (!group.folded) continue;
    for (const parentId of group.parentIds) {
      addFolded(parentId, group.entityType, group.memberIds);
    }
  }

  // --- Display vertices --------------------------------------------------
  const displayVertices = new Map<string, PowerFlowVertex>();
  for (const vertex of graph.vertices) {
    if (collapsedIds.has(vertex.id)) continue;
    displayVertices.set(vertex.id, vertex);
  }
  for (const group of groups) {
    if (group.expanded || group.folded) continue;
    displayVertices.set(group.id, syntheticVertex(group, verticesById));
  }

  // --- Links -------------------------------------------------------------
  let displayTreeLinks = rewriteLinks(treeLinks, groupOf).filter(
    link => !foldedGroupIds.has(link.target) && !foldedGroupIds.has(link.source)
  );
  let displayCycleLinks = rewriteLinks(cycleLinks, groupOf).filter(
    link => !foldedGroupIds.has(link.target) && !foldedGroupIds.has(link.source)
  );

  // --- Terminal buses ----------------------------------------------------
  // Folding racks leaves the RPPs with nothing drawn beneath them. Ninety
  // light bars with no taps are the same clutter one rank up, so the ones
  // that share a feed collapse the same way the racks did: `RPP × 3` under
  // its PDU, exactly what the mock writes. The badge carries the folded rack
  // count of all its members, so nothing it stands in for goes unstated.
  if (fold && groupBuses) {
    const drawnChildren = new Map<string, number>();
    const drawnParents = new Map<string, string[]>();
    for (const link of displayTreeLinks) {
      drawnChildren.set(link.source, (drawnChildren.get(link.source) ?? 0) + 1);
      const parents = drawnParents.get(link.target) ?? [];
      parents.push(link.source);
      drawnParents.set(link.target, parents);
    }

    const busBuckets = new Map<
      string,
      { entityType: string; parentIds: string[]; memberIds: string[] }
    >();
    for (const [id, vertex] of Array.from(displayVertices.entries())) {
      if (vertex.role !== 'bus') continue;
      if ((drawnChildren.get(id) ?? 0) > 0) continue;
      // Only a bus that *had* loads and lost them to the fold is terminal in
      // this sense. A bus with nothing beneath it in the payload is a data gap
      // the diagnostics already report, and hiding it in a badge would bury it.
      if (!foldedLoadsOf.has(id)) continue;
      const parentIds = (drawnParents.get(id) ?? []).slice().sort();
      if (parentIds.length === 0) continue;
      const key = `group:${vertex.entityType}:${parentIds.join('+')}`;
      const bucket = busBuckets.get(key);
      if (bucket) bucket.memberIds.push(id);
      else busBuckets.set(key, { entityType: vertex.entityType, parentIds, memberIds: [id] });
    }

    const busGroups: PowerFlowLoadGroup[] = Array.from(busBuckets.entries())
      .filter(([, bucket]) => bucket.memberIds.length >= minGroupSize)
      .map(([id, bucket]) => ({
        id,
        entityType: bucket.entityType,
        memberIds: bucket.memberIds.slice().sort(),
        parentIds: bucket.parentIds,
        rank: bucket.memberIds.reduce((max, id2) => Math.max(max, rankOf.get(id2) ?? 0), 0),
        expanded: expanded.has(id),
        kind: 'bus' as const,
        folded: false,
      }))
      .sort((a, b) => (a.id < b.id ? -1 : 1));

    const busGroupOf = new Map<string, string>();
    for (const group of busGroups) {
      groups.push(group);
      if (group.expanded) continue;
      for (const memberId of group.memberIds) {
        busGroupOf.set(memberId, group.id);
        groupOf.set(memberId, group.id);
        collapsedIds.add(memberId);
        displayVertices.delete(memberId);
        // The badge inherits what its members were standing over.
        for (const folded of foldedLoadsOf.get(memberId) ?? []) {
          addFolded(group.id, folded.entityType, folded.memberIds);
        }
      }
      displayVertices.set(group.id, syntheticVertex(group, verticesById));
    }
    groups.sort((a, b) => (a.id < b.id ? -1 : 1));

    if (busGroupOf.size > 0) {
      displayTreeLinks = rewriteLinks(displayTreeLinks, busGroupOf);
      displayCycleLinks = rewriteLinks(displayCycleLinks, busGroupOf);
    }
  }

  const groupsById = new Map(groups.map(group => [group.id, group]));

  // --- Ranks -------------------------------------------------------------
  const displayRankOf = new Map<string, number>();
  for (const [id, vertex] of Array.from(displayVertices.entries())) {
    const group = groupsById.get(id);
    displayRankOf.set(id, group ? group.rank : (rankOf.get(vertex.id) ?? 0));
  }

  const maxRank = Array.from(displayRankOf.values()).reduce((max, rank) => Math.max(max, rank), 0);
  const ranks: string[][] = Array.from(
    { length: displayVertices.size > 0 ? maxRank + 1 : 0 },
    () => []
  );
  for (const [id, rank] of Array.from(displayRankOf.entries())) ranks[rank]?.push(id);
  for (const rank of ranks) rank.sort();

  return {
    groups,
    groupsById,
    groupOf,
    collapsedIds,
    verticesById: displayVertices,
    ranks,
    rankOf: displayRankOf,
    treeLinks: displayTreeLinks,
    cycleLinks: displayCycleLinks,
    foldedLoadsOf,
  };
};

/**
 * The vertex a group stands in for.
 *
 * It inherits the members' symbol and voltage class — a group of racks is still
 * drawn and weighted as racks — and its `in` is the parent set, so anything
 * reading adjacency off the display sees the group fed exactly where its
 * members were. `out` is empty by construction: only tree leaves are grouped.
 */
const syntheticVertex = (
  group: PowerFlowLoadGroup,
  verticesById: ReadonlyMap<string, PowerFlowVertex>
): PowerFlowVertex => {
  const exemplar = verticesById.get(group.memberIds[0]);
  return {
    id: group.id,
    entityType: group.entityType,
    // A badge is terminal on the drawing whatever its members were: a run of
    // RPPs that lost their racks to the fold is drawn as one box, not one bar.
    role: 'load',
    symbol: exemplar?.symbol ?? 'chip',
    voltageClass: exemplar?.voltageClass ?? 'unknown',
    in: group.parentIds.slice(),
    out: [],
  };
};

/**
 * Point every link at the group that replaced its endpoint.
 *
 * Rewriting collapses the fan-out: twelve `RPP-A1 → RACK-n` links become one
 * `RPP-A1 → group:…` conductor, which is the whole point — twelve conductors
 * converging on one box is the clutter, not the information. `relTypes` are
 * merged so the surviving conductor still reports every predicate that produced
 * it, and a link whose two ends land in the same group is dropped rather than
 * drawn as a loop on the count badge.
 */
const rewriteLinks = (
  links: readonly PowerFlowLink[],
  groupOf: ReadonlyMap<string, string>
): PowerFlowLink[] => {
  if (groupOf.size === 0) return links.slice();

  const merged = new Map<string, Set<string>>();
  for (const link of links) {
    const source = groupOf.get(link.source) ?? link.source;
    const target = groupOf.get(link.target) ?? link.target;
    if (source === target) continue;

    const id = `${source}->${target}`;
    const existing = merged.get(id);
    if (existing) for (const type of link.relTypes) existing.add(type);
    else merged.set(id, new Set(link.relTypes));
  }

  return Array.from(merged.entries())
    .map(([id, relTypes]) => {
      const [source, target] = id.split('->');
      return { id, source, target, relTypes: Array.from(relTypes).sort() };
    })
    .sort((a, b) => (a.id < b.id ? -1 : 1));
};

/**
 * The identity display: every vertex drawn as itself.
 *
 * For callers that want the stage's shape without its transform — tests
 * pinning layout behaviour that has nothing to do with grouping, and the
 * "expand everything" path.
 */
export const displayWithoutCollapse = (
  graph: PowerFlowGraph,
  ranking: PowerFlowRanking
): PowerFlowDisplay =>
  collapsePowerFlow(graph, ranking, { minGroupSize: Number.POSITIVE_INFINITY });
